import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { FormControl, IconButton, InputAdornment, TextField, TextFieldPropsSizeOverrides, Tooltip, Typography } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
import { OverridableStringUnion } from '@mui/types';
import { FormikProps, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { Label } from '../styles';
import { DeepKeyOf } from './type';

interface commonInputProps<T> {
    formik: FormikProps<T>;
    fieldPath: DeepKeyOf<T>;
    type?: 'number' | 'text' | 'password';
    disabled?: boolean;
    handleOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnRead?: (value: string | number) => string | number;
    sx?: SxProps<Theme>;
    label?: string;
    TooltipText?: string;
    bottomLabel?: string;
    placeholder?: string;
    endAdornment?: string | React.ReactNode;
    multiline?: boolean;
    boxSize?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
    autoFillPlaceholder?: boolean;
    defaultValue?: string | number;
}

export const CommonInput = <T,>(props: commonInputProps<T>): JSX.Element => {
    const {
        formik,
        fieldPath = '',
        type = 'text',
        handleOnChange,
        handleOnRead,
        disabled = false,
        sx = {},
        placeholder = '',
        multiline = false,
        label,
        TooltipText,
        bottomLabel,
        endAdornment,
        boxSize,
        autoFillPlaceholder = false,
        defaultValue
    } = props;
    const { touched, errors, getFieldProps } = formik;

    const { t } = useTranslation();
    const defaultFormControlStyles: SxProps<Theme> = [{ width: '100%' }, ...(Array.isArray(sx) ? sx : [sx])];

    const getValue = (): string | number => {
        const value = getFieldProps(fieldPath).value;

        if (value === null || value === undefined) {
            return defaultValue ? defaultValue : '';
        }
        if (handleOnRead) {
            const readValue = handleOnRead(value);
            return readValue;
        }

        if (typeof value === 'number') {
            const sanitizedValue = Number(String(value).replace(/^0+/, ''));
            return sanitizedValue;
        }
        if (typeof value === 'string') {
            return value;
        }
        return '';
    };

    const handleValueOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;

        if (handleOnChange) {
            handleOnChange(e);
            return;
        }
        // if type is number, convert to number
        if (type === 'number') {
            // is NaN, return
            if (isNaN(Number(e.target.value))) {
                return;
            }
            // Remove leading zeros
            const sanitizedValue = inputValue.startsWith('0') && inputValue.length > 1 ? inputValue.replace(/^0+/, '') : inputValue;
            formik.setFieldValue(fieldPath, Number(sanitizedValue));
            e.target.value = sanitizedValue;
            return;
        }
        // if type is text, set value as is
        formik.setFieldValue(fieldPath, e.target.value);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        if (!placeholder) return;
        if (inputValue) return;
        if (autoFillPlaceholder) {
            const currentValue = getFieldProps(fieldPath).value;
            if (currentValue === '' || currentValue === null || currentValue === undefined) {
                if (type === 'number') {
                    if (!isNaN(Number(placeholder))) {
                        formik.setFieldValue(fieldPath, Number(placeholder));
                    }
                } else {
                    formik.setFieldValue(fieldPath, placeholder);
                }
            }
        }
    };

    return (
        <>
            <FormControl sx={defaultFormControlStyles}>
                {(label || TooltipText) && (
                    <Label
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {label ? t(label) : '\u00A0'}
                        {TooltipText && (
                            <Tooltip title={t(TooltipText)}>
                                <IconButton
                                    sx={{
                                        height: '100%',
                                        padding: 0
                                    }}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Label>
                )}
                <TextField
                    disabled={disabled}
                    multiline={multiline}
                    placeholder={t(placeholder)}
                    type={type}
                    inputMode={type === 'number' ? 'numeric' : 'text'}
                    name={fieldPath}
                    value={getValue()}
                    size={boxSize}
                    onWheel={(e) => {
                        if (type === 'number' && document.activeElement === e.currentTarget.querySelector('input')) {
                            e.currentTarget.querySelector('input')?.blur();
                        }
                    }}
                    slotProps={{
                        input: {
                            endAdornment: endAdornment ? (
                                typeof endAdornment === 'string' ? (
                                    <InputAdornment position="end">{t(endAdornment)}</InputAdornment>
                                ) : (
                                    <InputAdornment position="end">{endAdornment}</InputAdornment>
                                )
                            ) : null
                        }
                    }}
                    onChange={handleValueOnChange}
                    onFocus={handleFocus}
                    onBlur={formik.handleBlur}
                    error={Boolean(getIn(touched, fieldPath) && getIn(errors, fieldPath))}
                    helperText={getIn(touched, fieldPath) && getIn(errors, fieldPath)}
                />
                {bottomLabel && (
                    <Typography variant="body2" color="textSecondary">
                        {bottomLabel}
                    </Typography>
                )}
            </FormControl>
        </>
    );
};
